import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpenIcon, LibraryIcon, MessageSquareIcon, StarIcon, UserXIcon } from 'lucide-react';
import { AfterTideLayout } from '../../components/AfterTide/AfterTideLayout';
import { AfterTideBookCard } from '../../components/AfterTide/AfterTideBookCard';
import { AuthorHero } from '../../components/shared/AuthorHero';
import { SectionHeader } from '../../components/shared/SectionHeader';
import { EmptyState } from '../../components/shared/EmptyState';
import { useBrand } from '../../hooks/useBrand';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { aftertideAuthors, aftertideBooks, aftertideSeries, aftertideReviews } from '../../data/aftertideBooksData';

/**
 * AfterTide author profile — a data adapter only. It resolves the AfterTide
 * author, books, series and reviews, then renders LitLaunch's shared author
 * structure (AuthorHero + SectionHeader sections). AfterTideLayout is kept
 * because it enforces the 18+ age gate, a behaviour difference.
 */
export default function AfterTideAuthorProfile() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    theme,
    content
  } = useBrand();
  const dk = useDarkClasses();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const author = aftertideAuthors.find(a => a.slug === slug);
  if (!author) {
    return <AfterTideLayout data-id="element-4633" data-mp="3elksapxzc20n">
        <EmptyState icon={UserXIcon} title="Author Not Found" body="The author you're looking for doesn't exist or has been removed." action={{
        label: 'Browse Catalog',
        to: '/aftertide/explore'
      }} className="min-h-[60vh]" data-id="element-4634" data-mp="3el16uz94evhv" />
      </AfterTideLayout>;
  }
  const books = aftertideBooks.filter(b => (b.authorSlug || b.author.toLowerCase().replace(/\s+/g, '-')) === author.slug);
  const series = aftertideSeries.filter(s => s.authorSlug === author.slug);
  const bookIds = new Set(books.map(b => b.id));
  const reviews = aftertideReviews.filter(r => bookIds.has(r.bookId)).slice(0, 6);
  const avgRating = books.length ? (books.reduce((sum, b) => sum + b.rating, 0) / books.length).toFixed(1) : '—';
  return <AfterTideLayout data-id="element-4635" data-mp="3pe0ibfbq4n4y">
      <div className={`min-h-screen pb-20 ${dk.page}`} data-id="element-4636" data-mp="3pdh8klw89520">
        <AuthorHero name={author.name} avatar={author.avatar} bio={author.bio} genres={author.genres} leadBadge={author.tierRange} showContact={false} stats={[{
        value: avgRating,
        label: 'Rating',
        star: true
      }, {
        value: books.length,
        label: 'Books'
      }, {
        value: series.length,
        label: 'Series'
      }]} data-id="element-4637" data-mp="3uc2262gz4qhh" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 pt-8" data-id="element-4638" data-mp="3bmvpox6rwv44">
          {series.length > 0 && <section data-id="element-4639" data-mp="ly35szq3b33g">
              <SectionHeader title={`Series by ${author.name}`} icon={LibraryIcon} data-id="element-4640" data-mp="qv3ap7b7dzsz" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4641" data-mp="2bddvf4ccjxws">
                {series.map(s => <Link key={s.id} to={content.routes.series(s.id)} className={`rounded-xl p-6 border transition-colors group ${dk.card} ${theme.hero.hoverBorder}`} data-id="element-4642" data-mp="26f2lyupyrpf3">
                    <div className="flex justify-between items-start gap-4 mb-3" data-id="element-4643" data-mp="1wmdm6x3rzzzl">
                      <h3 className={`text-xl font-bold transition-colors ${dk.textHeading} ${theme.hero.hoverText}`} data-id="element-4644" data-mp="1snxpu2lt1f9i">
                        {s.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium whitespace-nowrap ${theme.tagRadius} ${dk.badgeNeutral}`} data-id="element-4645" data-mp="1dpdk6f5fjlcf">
                        {s.bookIds.length} Books
                      </span>
                    </div>
                    <p className={`text-sm mb-4 line-clamp-2 ${dk.textSecondary}`} data-id="element-4646" data-mp="381ad6cjisz9l">
                      {s.description}
                    </p>
                    <span className={`text-xs font-bold uppercase tracking-wider ${theme.accent.icon}`} data-id="element-4647" data-mp="3l3fi0d23tvnc">
                      {s.tierRange}
                    </span>
                  </Link>)}
              </div>
            </section>}

          <section data-id="element-4648" data-mp="2xdbiohvy8i25">
            <SectionHeader title={`Books by ${author.name}`} icon={BookOpenIcon} data-id="element-4649" data-mp="2xc0c6ab4cw7c" />
            {books.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6" data-id="element-4650" data-mp="326ebuowtdpht">
                {books.map(book => <div key={book.id} className="h-[280px] sm:h-[320px]" data-id="element-4651" data-mp="2sdp341snepq6">
                    <AfterTideBookCard book={book} data-id="element-4652" data-mp="2eepbqmdr3wqb" />
                  </div>)}
              </div> : <EmptyState icon={BookOpenIcon} title="No books available yet" data-id="element-4653" data-mp="2igkgy1ysyu9d" />}
          </section>

          {reviews.length > 0 && <section data-id="element-4654" data-mp="1zu4kfeofl4q0">
              <SectionHeader title="Reader Reviews" icon={MessageSquareIcon} data-id="element-4655" data-mp="1ut6zwvfy7ylc" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4656" data-mp="1uva028atf83s">
                {reviews.map(review => {
              const book = books.find(b => b.id === review.bookId);
              return <article key={review.id} className={`rounded-xl p-6 border flex flex-col ${dk.card}`} data-id="element-4657" data-mp="2iqcnqcxvv5io">
                      <div className="flex justify-between items-start mb-3" data-id="element-4658" data-mp="2rj6kjo9dinka">
                        <div data-id="element-4659" data-mp="2rlam3zjsvahe">
                          <p className={`font-medium ${dk.textPrimary}`} data-id="element-4660" data-mp="2nhus7az47o8e">
                            {review.reviewer}
                          </p>
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-4661" data-mp="31hvqqjrwqjkp">
                            {new Date(review.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                          </p>
                        </div>
                        <div className="flex" aria-label={`${review.rating} out of 5 stars`} data-id="element-4662" data-mp="234i3udciaaum">
                          {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < review.rating ? `${theme.accent.icon} fill-current` : dk.textFaint}`} data-id="element-4663" data-mp="2cv3383007jel" data-mpi={i} />)}
                        </div>
                      </div>
                      {book && <p className={`text-xs mb-3 font-medium ${theme.accent.icon}`} data-id="element-4664" data-mp="rijjax64ks1m">
                          Reviewed: {book.title}
                        </p>}
                      <p className={`text-sm italic mb-4 ${dk.textSecondary}`} data-id="element-4665" data-mp="oxbrfkfecklx">
                        "{review.content}"
                      </p>
                      {review.tierContext && <span className={`mt-auto self-start px-2 py-1 text-xs ${theme.tagRadius} ${dk.badgeNeutral}`} data-id="element-4666" data-mp="f7inn5lhx9s8">
                          {review.tierContext}
                        </span>}
                    </article>;
            })}
              </div>
            </section>}
        </div>
      </div>
    </AfterTideLayout>;
}